import React, { useMemo } from 'react'
import { useProject } from "../../providers/ClientProjectProvider";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { usePosition } from "../../providers/ProjectPositionProvider";
import LoadingBanner from "../../components/LoadingBanner";
import PageNotFound from "../../components/errors/PageNotFound";
import PageError from "../../components/errors/PageError";
import ResourceNotFound from "../../components/errors/ResourceNotFound";
import { useAuth } from "../../providers/AuthProvider";
import { useAPI } from "../../config/API";

import CandidateProvider from "../../providers/CandidateProvider";
import TalentProvider, { useTalent } from '../../providers/TalentProvider';
import TalentCandidate from "../../models/TalentCandidate";
import ProfileLayoutWrapper from "./ProfileLayoutWrapper";

import {
    TalentProfileDetails,
    TalentProfileEducation,
    TalentProfileMatchScore,
    TalentProfileHireConfidence,
    TalentProfileResumeScore,
    TalentProfileWorkHistory,
    TalentProfileIndustryExperience,
    TalentProfileBrandExperience,
    TalentProfileWorkHighlights,
    TalentProfileSkills,
    TalentProfileReferences,
    TalentProfileCertifications,
    TalentProfileAwards,
    TalentProfileWrippleProjects,
    TalentProfileInterviews,
    TalentProfileWorkPreferences,
    TalentProfileTeams,
    TalentProfileAiExperience,
    TalentProfileRoles,
} from './sections';
import TalentProfileWorkStatus from "./sections/TalentProfileWorkStatus";

const components = {
    talent_disciplines: { title: 'Roles', element: TalentProfileRoles, filter: items => items.filter(ref => ref.is_approved) },
    ai_experience: { title: 'AI Experience', element: TalentProfileAiExperience, alwaysShow: true },
    industries_worked: { title: 'Industry Experience', element: TalentProfileIndustryExperience },
    brands: { title: 'Brand Experience', element: TalentProfileBrandExperience },
    projects: { title: 'Wripple Projects', element: TalentProfileWrippleProjects },
    key_clients: { title: 'Work Highlights', element: TalentProfileWorkHighlights },
    skills: { title: 'Skills', element: TalentProfileSkills },
    talent_references: { title: 'References', element: TalentProfileReferences, filter: items => items.filter(ref => !!ref.is_confirmed) },
    schools: { title: 'Education', element: TalentProfileEducation },
    jobs: { title: 'Work History', element: TalentProfileWorkHistory },
    certifications: { title: 'Certifications', element: TalentProfileCertifications },
    awards: { title: 'Awards', element: TalentProfileAwards },
    work_preferences: { title: 'Preferences', element: TalentProfileWorkPreferences, alwaysShow: true },
    work_status: { title: 'Work Status', element: TalentProfileWorkStatus, alwaysShow: true },
    talent_video_interviews: { title: 'Interviews', element: TalentProfileInterviews },
    positions: { title: 'Teams', element: TalentProfileTeams },
}


const TalentProfileLayout = ({ isLoading, children, isCandidate, ...props }) => {
    const location = useLocation();
    const { talent, criteria } = useTalent();
    const { auth } = useAuth();


    const profileNavItems = useMemo(() => {
        if (isLoading || !talent) return [];

        let items = [];

        if (talent.matching) {
            items.push({
                title: 'Match Score',
                element: TalentProfileMatchScore
            })
        }

        // The AI's resume read, on its own. Gated the same way as the sections
        // below but on a different condition, and deliberately so: this one is
        // scored from the moment a talent matches, which is the whole point of
        // separating it. A client lead decides whether to invite someone long
        // before there is a proposal to assess, and this is the only AI signal
        // available at that moment.
        if (auth?.canManageClients() && TB.isDefined(talent.resumeMatchScore?.())) {
            items.push({
                title: 'AI Resume Score',
                element: TalentProfileResumeScore
            })
        }

        // Internal-only breakdown of the AI hire assessment. Both gates matter:
        // the data only exists once the talent has been assessed as a candidate
        // for a position, and the section is staff-facing while Hire Confidence
        // is being validated.
        if (auth?.canManageClients() && TB.isDefined(talent.hireConfidenceScore?.())) {
            items.push({
                title: 'Hire Confidence',
                element: TalentProfileHireConfidence
            })
        }

        Object.keys(components).forEach(key => {
            let sectionItems = talent[key] || [];

            // hide ai_experience component if talent hasnt filled out yet
            if (key === 'ai_experience' && talent.ai_focus_level === null) {
                return;
            }
            // hide talent_disciplines unless user is a client-manager or the talent themselves
            if (key === 'talent_disciplines' && !auth?.canManageClients() && auth?.id !== talent?.id) {
                return;
            }
            // Custom filter option
            if (components[key].filter) {
                sectionItems = components[key].filter(sectionItems);
            }
            if (components[key]?.alwaysShow || sectionItems.length) {
                items.push({ ...components[key], items: sectionItems, criteria })
            }
        })
        return items;
    }, [isLoading, auth])


    return (
        <ProfileLayoutWrapper
            sections={profileNavItems}
            isLoading={isLoading}
            type={location.pathname?.includes('bidding') ? "Candidate" : "Talent"}>
            <TalentProfileDetails isCandidate={isCandidate}>
                {children}
            </TalentProfileDetails>
        </ProfileLayoutWrapper>

    )
}


const CandidateProfile = (props) => {

    const { project, isLoading } = useProject();
    const { position } = usePosition();
    const { profileId, '*': tab } = useParams();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    if (!project && !location.pathname.indexOf('browse')) {
        return <PageNotFound />
    }


    const {
        data: candidate,
        isFetching,
        error,
        refetch
    } = useAPI([
        'fetchTalent', {
            profileId,
            position: position?.id,
            project: project?.id
        }
    ], {
        enabled: !isLoading && !TB.isNaN(parseInt(profileId)),
        select: (data) => TalentCandidate.make(data)
    })

    const exitLink = useMemo(() => {
        if (project) {
            return {
                ...location,
                pathname: project.detailLink()
            }
        }
        if (searchParams.has("agencyId")) {

        }
        if (location.pathname.indexOf('browse')) {
            return {
                ...location,
                pathname: '/client/browse',
            }
        }
    }, [location.pathname, !!project])
    if (error) {
        return <PageError
            exitLink={exitLink} exitText={'Take me back'}
        />
    }
    if (!isFetching && !isLoading && !candidate) {
        return <ResourceNotFound type="User" exitLink={exitLink}
            exitText={project ? 'Return to project' : 'Return to browse'} />
    }

    if (isFetching || !candidate) {
        return (
            <div className="flex-grow flex flex-col items-stretch justify-start py-10 px-2 sm:px-6">
                <LoadingBanner />
            </div>
        )
    }

    return (
        <CandidateProvider
            candidate={candidate}
            criteria={position}
            project={project}
        >
            <TalentProfileLayout isLoading={isFetching} />
        </CandidateProvider>
    )
}

const TalentProfile = (props) => {

    const { profileId, '*': tab } = useParams();
    const location = useLocation();

    if (!location.pathname.indexOf('browse') && !location.pathname.indexOf('add-talent')) {
        return <PageNotFound />
    }


    const {
        data: candidate,
        isFetching,
        error,
        refetch
    } = useAPI([
        'fetchTalent', {
            profileId
        }
    ], {
        enabled: props.browseResults?.length > 0 && !TB.isNaN(parseInt(profileId)),
    })

    const profiledWithMatching = useMemo(() => {
        if (!props.browseResults?.length) return;
        let matchedProfile = TB.find(props.browseResults, ['id', parseInt(profileId)])

        if (!matchedProfile) return;

        if (!isFetching && candidate) {
            matchedProfile = {
                ...matchedProfile, ...candidate
            }
        }
        return TalentCandidate.make(matchedProfile);
    }, [isFetching, candidate, props.browseResults]);

    const exitLink = useMemo(() => {
        if (location.pathname.indexOf('browse')) {
            return {
                ...location,
                pathname: '/client/browse',
            }
        }
    }, [location.pathname])
    if (error) {
        return (
            <div className="w-full flex-1 h-full flex flex-row items-center justify-center">
                <PageError
                    exitLink={exitLink} exitText={'Take me back'}
                />
            </div>
        )
    }

    if (!profiledWithMatching) {
        return (
            <div className="w-full flex-1 h-full flex flex-row items-center justify-center">
                <ResourceNotFound type="User" exitLink={exitLink} exitText="Return to browse" />
            </div>
        )
    }

    return (
        <TalentProvider
            talent={profiledWithMatching}
            criteria={props.criteria}>
            <TalentProfileLayout isLoading={isFetching} />
        </TalentProvider>
    )
}

const ClientViewTalentProfile = (props) => {

    const { profileId } = useParams();
    const location = useLocation();

    const { auth } = useAuth();
    const { "*": tab } = useParams();

    const {
        data: candidate,
        isFetching: isLoading,
        error,
        refetch
    } = useAPI([
        'fetchTalent', {
            profileId
        }
    ], {
        select: user => {
            if (auth.id === user.id) {
                return TalentCandidate.make({ ...auth, ...user })
            }
            return TalentCandidate.make(user)
        },
        enabled: !TB.isNaN(parseInt(profileId))
    })

    if (!isLoading && error) {
        return (
            <PageError exitLink={'/talent/dashboard'} exitText={'Take me back'} />
        )
    }

    if (!isLoading && !candidate) {
        return <ResourceNotFound type="User" exitLink={'/talent/dashboard'} exitText="Return to browse" />
    }

    return (
        <TalentProvider talent={candidate}>
            <TalentProfileLayout isLoading={isLoading} />
        </TalentProvider>
    )
}

export default CandidateProfile;

export {
    CandidateProfile,
    TalentProfile,
    ClientViewTalentProfile
}